'use client';

import { useState, useEffect, useRef } from 'react';
import { UserPlus, UserCheck } from 'lucide-react';
import clsx from 'clsx';
import { useFollowToggle } from '@/lib/hooks/useFollow';
import { useSession } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';

interface FollowButtonProps {
  objectId: number;
  objectType: 'entity' | 'item' | 'initiative' | 'content';
  following: boolean;
  className?: string;
  showLabel?: boolean;
}

export function FollowButton({
  objectId,
  objectType,
  following,
  className,
  showLabel = false,
}: FollowButtonProps) {
  const { data: session } = useSession();
  const { showSignInModal } = useSignInModal();
  const { toggleFollow, isLoading } = useFollowToggle(objectId, objectType);
  const pendingFollowRef = useRef(false);

  // Local optimistic state for immediate visual feedback
  const [optimisticFollowing, setOptimisticFollowing] = useState(following);

  // Sync with prop when it changes (e.g. from cache invalidation)
  useEffect(() => {
    setOptimisticFollowing(following);
  }, [following]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Require authentication – show sign-in modal instead of navigating away
    if (!session?.user) {
      pendingFollowRef.current = true;
      showSignInModal({
        description: (
          <div className="font-bold p-6">
            <div className="text-lg">Sign in to follow</div>
          </div>
        ),
        preferFullscreenForNewUsers: false,
        onSuccess: () => {
          if (pendingFollowRef.current) {
            pendingFollowRef.current = false;
            setOptimisticFollowing(true);
            toggleFollow(false);
          }
        },
      });
      return;
    }

    // Optimistically toggle local state immediately
    setOptimisticFollowing(!optimisticFollowing);
    toggleFollow(optimisticFollowing);
  };

  const Icon = optimisticFollowing ? UserCheck : UserPlus;

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={clsx(
        'flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-all cursor-pointer',
        optimisticFollowing
          ? 'bg-yellow-50 border-yellow-300 text-yellow-800 hover:bg-yellow-100'
          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
        isLoading && 'opacity-50 cursor-not-allowed',
        className
      )}
      aria-label={optimisticFollowing ? 'Unfollow' : 'Follow'}
      title={optimisticFollowing ? 'Unfollow' : 'Follow'}
    >
      <Icon className="w-4 h-4" />
      {showLabel && <span>{optimisticFollowing ? 'Following' : 'Follow'}</span>}
    </button>
  );
}
